import { FC } from "react";
import { Trans } from "@lingui/macro";
import { Done } from "src/icons/Done";
import { VisaIcon } from "src/icons/VisaIcon";
import { PaypalIcon } from "src/icons/PaypalIcon";
import { MastercardIcon } from "src/icons/MastercardIcon";
import { SofortIcon } from "src/icons/SofortIcon";
import { ThuiswinkelWaarborgIcon } from "src/icons/ThuiswinkelWaarborgIcon";
import { IdealIcon } from "src/icons/IdealIcon";
import { BancontactIcon } from "src/icons/BancontactIcon";
import { ApplePayIcon } from "src/icons/ApplePay";
import { AmexIcon } from "src/icons/Amex";
import { MaestroIcon } from "src/icons/Maestro";
import { CartaSiIcon } from "src/icons/CartaSi";
import { Przelewy24Icon } from "src/icons/Przelewy24";
import { PostepayIcon } from "src/icons/Postepay";
import { Div, Divider, StyledFooterSeoText } from "./FooterCopyright.styled";
import { Flex } from "@otrium/core";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { Locale } from "lib/Intl";
import { useMiscellaneous } from "src/hooks/useMiscellaneous";
import { LogoFooter } from "src/icons/LogoFooter";

interface Props {
  locale: Locale;
}

const FooterPaymentNl = () => {
  return (
    <>
      <div className="otrium-payment-logos flex-justify">
        <div>
          <IdealIcon />
        </div>
        <div>
          <BancontactIcon />
        </div>
        <div>
          <PaypalIcon />
        </div>
        <div>
          <VisaIcon />
        </div>
        <div>
          <MastercardIcon />
        </div>
      </div>

      <div className="otrium-payment-mark flex-justify hidden-mobile">
        <a
          rel="noopener noreferrer"
          href="https://www.thuiswinkel.org/leden/otrium-b.v/certificaat"
          target="_blank"
          className="thuiswinkel-waarborg-logo"
        >
          <ThuiswinkelWaarborgIcon />
        </a>
      </div>
    </>
  );
};

const FooterPaymentUk = () => {
  return (
    <>
      <div className="otrium-payment-logos flex-justify">
        <div>
          <PaypalIcon />
        </div>
        <div>
          <VisaIcon />
        </div>
        <div>
          <MastercardIcon />
        </div>
      </div>
    </>
  );
};

const FooterPaymentUs = () => {
  return (
    <>
      <div className="otrium-payment-logos flex-justify">
        <div>
          <PaypalIcon />
        </div>
        <div>
          <VisaIcon />
        </div>
        <div>
          <MastercardIcon />
        </div>
        <div>
          <AmexIcon />
        </div>
      </div>
    </>
  );
};

const FooterPaymentDe = () => {
  return (
    <Flex flex={1} justifyContent="space-between">
      <SofortIcon />
      <PaypalIcon />
      <VisaIcon />
      <MastercardIcon />
    </Flex>
  );
};

const FooterPaymentFr = () => {
  return (
    <Flex flex={1} justifyContent="space-between">
      <PaypalIcon />
      <VisaIcon />
      <MastercardIcon />
    </Flex>
  );
};

const FooterPaymentSe = () => {
  return (
    <Flex flex={1} justifyContent="space-between">
      <PaypalIcon />
      <VisaIcon />
      <MastercardIcon />
      <AmexIcon />
      <ApplePayIcon />
    </Flex>
  );
};

const FooterPaymentEs = () => {
  return (
    <Flex flex={1} justifyContent="space-between">
      <VisaIcon />
      <MastercardIcon />
      <PaypalIcon />
      <ApplePayIcon />
      <MaestroIcon />
      <AmexIcon />
    </Flex>
  );
};

const FooterPaymentIt = () => {
  return (
    <Flex flex={1} justifyContent="space-between">
      <VisaIcon />
      <MastercardIcon />
      <PaypalIcon />
      <ApplePayIcon />
      <AmexIcon />
      <CartaSiIcon />
      <PostepayIcon />
    </Flex>
  );
};

const FooterPaymentPl = () => {
  return (
    <Flex flex={1} justifyContent="space-between">
      <VisaIcon />
      <MastercardIcon />
      <PaypalIcon />
      <AmexIcon />
      <ApplePayIcon />
      <Przelewy24Icon />
    </Flex>
  );
};

const FooterCopyright: FC<Props> = ({ locale }) => {
  const { data: miscellaneousData } = useMiscellaneous();

  return (
    <Div>
      <div className="container">
        <div className="otrium-features">
          <ul>
            {miscellaneousData?.miscellaneous?.footer?.map((footer, index) => (
              <li key={index}>
                <Done />
                <span>{footer}</span>
              </li>
            ))}
          </ul>
        </div>

        {["nl", "en"].includes(locale) && (
          <div className="otrium-payment otrium-payment-mark visible-mobile">
            <div className="flex-evenly">
              <a
                rel="noopener noreferrer"
                href="https://www.thuiswinkel.org/leden/otrium-b.v/certificaat"
                target="_blank"
              >
                <ThuiswinkelWaarborgIcon />
              </a>
            </div>
          </div>
        )}
        <div className="otrium-payment">
          {["nl", "en", "nl-be"].includes(locale) && <FooterPaymentNl />}
          {["en-gb"].includes(locale) && <FooterPaymentUk />}
          {["en-us"].includes(locale) && <FooterPaymentUs />}
          {["de", "de-at"].includes(locale) && <FooterPaymentDe />}
          {["fr"].includes(locale) && <FooterPaymentFr />}
          {["en-se", "en-dk"].includes(locale) && <FooterPaymentSe />}
          {["es-es"].includes(locale) && <FooterPaymentEs />}
          {["it-it"].includes(locale) && <FooterPaymentIt />}
          {["pl-pl"].includes(locale) && <FooterPaymentPl />}
        </div>

        <StyledFooterSeoText id="footer-seo-text-portal" />

        <div className="footer-copyright-3 flex-justify">
          <div className="footer-copyright-text-logo">
            <LogoFooter color={"black"} />
            <div className="footer-copyright-text">
              © 2016-{new Date().getFullYear()} Otrium,{" "}
              <Trans>except certain content provided by third parties</Trans>
            </div>
          </div>

          {["en-us"].includes(locale) && (
            <>
              <div className="footer-copyright-privacy">
                <a
                  rel="noopener noreferrer"
                  href="https://lookbook.otrium.com/files/Otrium_California_Resident_Privacy_Notice.pdf"
                >
                  CCPA (Do not sell my personal Information)
                </a>
              </div>
              <Divider />
            </>
          )}

          {["en-us"].includes(locale) && (
            <>
              <div className="footer-copyright-privacy">
                <a rel="noopener noreferrer" href="/terms-of-use/">
                  Terms of Sale
                </a>
              </div>
              <Divider />
            </>
          )}

          {["en-us"].includes(locale) ? (
            <div className="footer-copyright-privacy">
              <a rel="noopener noreferrer" href="/privacy-notice/">
                Privacy Notice
              </a>
            </div>
          ) : (
            <div className="footer-copyright-privacy">
              <a
                rel="noopener noreferrer"
                href={getI18nRoute({ route: "/privacy-statement", locale })}
              >
                <Trans>Privacy</Trans>
              </a>
            </div>
          )}
          <Divider />

          <div className="footer-copyright-terms">
            <a
              rel="noopener noreferrer"
              href={getI18nRoute({ route: "/terms-and-conditions", locale })}
            >
              <Trans>Terms & Conditions</Trans>
            </a>
          </div>
          <Divider />

          <div className="footer-copyright-cookies">
            <a rel="noopener noreferrer" href="/cookies">
              <Trans>Cookies</Trans>
            </a>
          </div>

          {["fr"].includes(locale) && (
            <>
              <Divider />
              <div className="footer-copyright-cookies">
                <a rel="noopener noreferrer" href="/mentions-legales/">
                  Mentions légales
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </Div>
  );
};
export default FooterCopyright;
